import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useHistory } from 'react-router';
import { IonContent, IonIcon, IonImg, IonPage, IonToggle } from '@ionic/react';
import { useDispatch, useSelector } from 'react-redux';
import { firstValueFrom } from 'rxjs';
import {
  shareOutline,
  heartOutline,
  ellipsisVertical,
  chevronForwardOutline,
  chevronDownOutline,
} from 'ionicons/icons';
import { ICasinoSearchData, IState, IUserInfo } from 'appRedux/createStore';
import { ISearchInfo } from 'reduxStore/models/casinoModels';
import { ICasinoSchema } from 'appRedux/models/casinoModels';
import useFavorites from 'common/reactHooks/useFavorites';
import usePlaylist, { isIncludedInPlaylist } from 'common/reactHooks/usePlaylist';
import { isIncludedInFavorites } from 'pages/Favorites/components/LikeButton';
import { FavoriteIcon } from 'components/FavoriteIcon/FavoriteIcon';
import { PlaylistIcon } from 'components/PlaylistIcon/PlaylistIcon';
import { InfoIcon } from 'components/InfoIcon/InfoIcon';
import { selectFavoritesList, selectPlaylist, selectUserId } from 'appRedux/selectors';
import { featureFlags } from 'config/featureFlags';
import { SlotReduxCommandCreator } from 'appRedux/actions/slotCommandCreator';
import useSlotDetailHistory from 'globalServices/useSlotDetailHistory';
import { MACHINE_MENU_POPOVER_WIDTH } from 'components/MachineActionsMenu/menuStyles';
import CasinoNavBar from 'components/CasinoNavBar';
import { CasinoReduxCommandCreator } from 'appRedux/actions/casinoCommandCreator';
import { parseValue, formatCurrency, formatPercentage } from 'common/include';
import { S3_PROFILE_BASE as S3_BASE, getCleanGameName } from 'common/common';
import { casinoIdListEnum } from 'common/commonEnum';
import { isJackpotInsight, mapNtileToFloormapHeat } from 'common/floormapHeat';
import { heatBeanRatings, floormapJackpotHeatBeanRatings } from 'config/HeatBeanRatings';
import { InsightValue } from 'common/types';
import {
  CasinoService,
  IGetMyPlayDayBody,
  IGetMyPlayResponse,
  IGetMyPlayYearBody,
  IMyPlayGamesPlayedCard,
  IMyPlayJackpotCard,
  IMyPlayKpis,
  MyPlayPeriodQuery,
} from 'services/casino';
import { searchRowDetailId } from 'services/insightCasino';
import MyPlayMarqueeText from './MyPlayMarqueeText';
import {
  isMyPlayInsight,
  myPlayBankText,
  hasMyPlayBankText,
  foldBlankTitlesIntoOther,
} from './myPlayInsight';
import { totalRtpLabel } from './myPlayRtp';
import {
  formatCasinoTodayHeader,
  formatMyPlayDateLabel,
  formatMyPlayVisitDate,
  resolveCasinoTimeZone,
} from 'lib/casinoLocalDate';
import MostPlayedTitlesDonut, { MostPlayedDonutSlice } from './MostPlayedTitlesDonut';

type FilterTab = 'today' | 'last_visit' | 'year_summary';
type GamesPlayedFilterId = Extract<
  InsightValue,
  'spins' | 'money_won' | 'avg_money_won_per_spin' | 'return' | 'money_played' | 'jackpot_value'
>;

interface GamePlayedItem {
  id: number;
  game_name: string;
  theme: string;
  /** Display: floorLocation (insight) or legacy slot. */
  slot_number: string;
  /** Machine id for favorites/playlist/Details (insight: mnum). */
  mnum: string;
  section: string;
  /** Insight casinos only (GetMyPlay Mongo machines). */
  bank?: string;
  date_group: string;
  spins: number;
  spins_ntile: number;
  money_won: number;
  money_won_ntile: number;
  jackpot_value: number;
  jackpot_value_ntile: number;
  money_played: number;
  money_played_ntile: number;
  return: number;
  return_ntile: number;
  avg_money_won_per_spin: number;
  avg_money_won_per_spin_ntile: number;
  jackpots: number;
}

const FILTER_TABS: { id: FilterTab; label: string }[] = [
  { id: 'today', label: 'Today' },
  { id: 'last_visit', label: 'Last visit' },
  { id: 'year_summary', label: "This Year's Summary" },
];

const GP_FILTERS: { id: GamesPlayedFilterId; label: string }[] = [
  { id: 'spins', label: 'Spins' },
  { id: 'money_won', label: '$ Won' },
  { id: 'avg_money_won_per_spin', label: '$ W/S' },
  { id: 'return', label: 'RTP' },
  { id: 'money_played', label: '$ Played' },
  { id: 'jackpot_value', label: '$ Jackpots' },
];

const GP_TOGGLE_FILTERS: GamesPlayedFilterId[] = ['money_won', 'avg_money_won_per_spin', 'return'];

const RTP_HOT_COLOR = '#22c55e';

// Keep the share wiring in place so the header control can be restored later.
const SHOW_SHARE = false;

type SubstatKey =
  | 'jackpot_value'
  | 'money_won'
  | 'money_played'
  | 'return'
  | 'spins'
  | 'avg_money_won_per_spin';

const SUBSTAT_LABEL: Record<SubstatKey, string> = {
  jackpot_value: '$ JKPT',
  money_won: '$ Won',
  money_played: '$ Played',
  return: 'RTP',
  spins: 'Spins',
  avg_money_won_per_spin: '$ W/S',
};

const getSubstats = (filter: GamesPlayedFilterId, jackpotsIncluded: boolean): SubstatKey[] => {
  switch (filter) {
    case 'spins':
      return ['jackpot_value', 'money_played', 'return', 'spins', 'avg_money_won_per_spin'];
    case 'money_won':
      return jackpotsIncluded
        ? ['jackpot_value', 'money_played', 'return', 'spins', 'avg_money_won_per_spin']
        : ['money_played', 'return', 'spins', 'avg_money_won_per_spin'];
    case 'money_played':
      return ['jackpot_value', 'money_won', 'return', 'spins', 'avg_money_won_per_spin'];
    case 'return':
      return jackpotsIncluded
        ? ['jackpot_value', 'money_won', 'money_played', 'return', 'spins']
        : ['money_won', 'money_played', 'return', 'spins'];
    case 'avg_money_won_per_spin':
      return jackpotsIncluded
        ? ['jackpot_value', 'money_won', 'money_played', 'return', 'spins']
        : ['money_won', 'money_played', 'return', 'spins'];
    case 'jackpot_value':
      return ['money_won', 'money_played', 'return', 'spins', 'avg_money_won_per_spin'];
  }
};

const GP_TOTAL_LABELS: Record<GamesPlayedFilterId, string> = {
  spins: 'Total Spins',
  money_won: 'Total $ Won',
  avg_money_won_per_spin: 'Total $ W/S',
  return: 'Total RTP',
  money_played: 'Total $ Played',
  jackpot_value: 'Total $ Jackpots',
};

const DONUT_COLORS = ['#c8f035', '#22c55e', '#06b6d4', '#3b82f6', '#f97316', '#a855f7', '#eab308'];

const EMPTY_KPIS: IMyPlayKpis = {
  jackpotsCount: 0,
  jackpotsValue: 0,
  spins: 0,
  winPerSpin: 0,
  moneyWon: 0,
};

const tabToPeriod = (tab: FilterTab): MyPlayPeriodQuery => (tab === 'year_summary' ? 'year' : tab);

const num = (v: unknown, fallback = 0): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

// Dates are casino-local (casinoSchema time zone; see lib/casinoLocalDate.ts).
// "Today" starts at midnight in the casino's zone, not UTC.
const formatPlayDateLabel = (
  playDate: string | undefined,
  period: MyPlayPeriodQuery,
  timeZone: string
): string => formatMyPlayDateLabel(playDate, period, timeZone);

const formatVisitDate = (iso: string | undefined, timeZone: string): string =>
  formatMyPlayVisitDate(iso, timeZone);

/** Header subtitle for Today tab — casino-local today (e.g. "Nov 1st" at 8:47 PM PT Nov 1). */
const formatTodayHeaderDate = (timeZone: string): string => formatCasinoTodayHeader(timeZone);

/** Prefer mostPlayed.playDate, else first gamesPlayed playDate (day body has no visits). */
const pickDayPlayDate = (dayBody: IGetMyPlayDayBody): string | undefined => {
  const fromMost = dayBody.mostPlayed?.playDate;
  if (fromMost) return String(fromMost);
  const list = Array.isArray(dayBody.gamesPlayed) ? dayBody.gamesPlayed : [];
  for (const card of list) {
    if (card?.playDate) return String(card.playDate);
  }
  return undefined;
};

const mapGamesPlayedCard = (
  card: IMyPlayGamesPlayedCard,
  index: number,
  period: MyPlayPeriodQuery,
  timeZone: string
): GamePlayedItem => ({
  id: index + 1,
  game_name: String(card.gameName ?? ''),
  theme: String(card.theme ?? ''),
  // Display floor location; APIs use mnum.
  slot_number: String(card.floorLocation ?? card.slot_number ?? card.mnum ?? '').trim(),
  mnum: String(card.mnum ?? '').trim(),
  section: String(card.section ?? ''),
  bank: card.bank != null ? String(card.bank) : undefined,
  date_group: formatPlayDateLabel(card.playDate, period, timeZone),
  spins: num(card.spins),
  spins_ntile: num(card.spinsNtile),
  money_won: num(card.moneyWon),
  money_won_ntile: num(card.moneyWonNtile),
  jackpot_value: num(card.jackpotValue),
  jackpot_value_ntile: num(card.jackpotValueNtile),
  money_played: num(card.moneyPlayed),
  money_played_ntile: num(card.moneyPlayedNtile),
  return: num(card.return),
  return_ntile: num(card.returnNtile),
  avg_money_won_per_spin: num(card.winPerSpin),
  avg_money_won_per_spin_ntile: num(card.winPerSpinNtile),
  jackpots: num(card.jackpots),
});

type JackpotView = {
  game_name: string;
  theme: string;
  /** Display: floorLocation (insight) or legacy slot. */
  slot_number: string;
  /** Machine id for favorites/playlist/Details (insight: mnum). */
  mnum: string;
  section: string;
  /** Insight casinos only (GetMyPlay Mongo machines). */
  bank?: string;
  jackpot_value: number;
  jackpot_value_ntile: number;
};

const mapJackpotCard = (card: IMyPlayJackpotCard | null | undefined): JackpotView | null => {
  if (!card) return null;
  return {
    game_name: String(card.gameName ?? ''),
    theme: String(card.theme ?? ''),
    slot_number: String(card.floorLocation ?? card.slot_number ?? card.mnum ?? '').trim(),
    mnum: String(card.mnum ?? '').trim(),
    section: String(card.section ?? ''),
    bank: card.bank != null ? String(card.bank) : undefined,
    jackpot_value: num(card.jackpotValue),
    jackpot_value_ntile: num(card.jackpotValueNtile),
  };
};

type MostPlayedView = {
  game_name: string;
  theme: string;
  /** Display: floorLocation (insight) or legacy slot. */
  slot_number: string;
  /** Machine id for favorites/playlist/Details (insight: mnum). */
  mnum: string;
  section: string;
  /** Insight casinos only (GetMyPlay Mongo machines). */
  bank?: string;
  spins: number;
  spins_ntile: number;
};

const mapMostPlayed = (card: IMyPlayGamesPlayedCard | null | undefined): MostPlayedView | null => {
  if (!card) return null;
  return {
    game_name: String(card.gameName ?? ''),
    theme: String(card.theme ?? ''),
    slot_number: String(card.floorLocation ?? card.slot_number ?? card.mnum ?? '').trim(),
    mnum: String(card.mnum ?? '').trim(),
    section: String(card.section ?? ''),
    bank: card.bank != null ? String(card.bank) : undefined,
    spins: num(card.spins),
    spins_ntile: num(card.spinsNtile),
  };
};

/** Prefer mnum for My Play actions; fall back to displayed slot_number (legacy). */
const myPlayDetailId = (item: { mnum?: string; slot_number: string }): string =>
  (item.mnum && item.mnum.trim()) || item.slot_number;

type CatalogNameTheme = { game_name: string; theme: string; section: string };

/**
 * Miller lock (SSC/RWC/ACH): FE join mnum → GetSearchData slot_number →
 * game_name + theme + section. Prefer non-empty catalog; never overwrite a
 * non-empty value with an empty one.
 * Insight casinos (truthy insightInstance): GetMyPlay already returns
 * name / theme / section / bank from Mongo machines, so catalogBySlot is left
 * empty and GetSearchData cannot clobber them.
 */
const enrichFromCatalog = <
  T extends { slot_number: string; game_name: string; theme: string; section: string }
>(
  item: T,
  catalogBySlot: Map<string, CatalogNameTheme>
): T => {
  const hit = catalogBySlot.get(String(item.slot_number).trim());
  if (!hit) return item;
  const pick = (current: string, fromCatalog: string): string => {
    const catalogVal = String(fromCatalog ?? '').trim();
    if (catalogVal) return fromCatalog;
    return current;
  };
  return {
    ...item,
    game_name: pick(item.game_name, hit.game_name),
    theme: pick(item.theme, hit.theme),
    section: pick(item.section, hit.section),
  };
};

type MostPlayedTitleRow = { name: string; spins: number };

/** Parse year mostPlayedTitles.titles whether Mongo stored array or JSON string of mnums. */
const parseMostPlayedTitles = (raw: unknown): MostPlayedTitleRow[] => {
  let list: unknown = raw;
  if (typeof list === 'string') {
    const trimmed = list.trim();
    if (!trimmed) return [];
    try {
      list = JSON.parse(trimmed);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(list)) return [];
  // Blank-named titles (no machine match) roll into 'Other' (mnum ignored).
  return foldBlankTitlesIntoOther(
    list.map((t) => {
      const row = t as { name?: unknown; spins?: unknown } | null | undefined;
      return {
        name: String(row?.name ?? ''),
        spins: num(row?.spins),
      };
    })
  );
};

/** Resolve title name: Other stays Other; else GetSearchData slot_number → game_name; miss → mnum. */
const resolveTitleName = (name: string, catalog: Map<string, CatalogNameTheme>): string => {
  const key = String(name).trim();
  if (!key) return '';
  if (key === 'Other') return 'Other';
  return catalog.get(key)?.game_name || key;
};

const isEmptyResponse = (res: IGetMyPlayResponse | null): boolean =>
  Boolean(res && typeof res === 'object' && (res as { empty?: boolean }).empty === true);

const kpisAreAllZero = (kpis: IMyPlayKpis | null | undefined): boolean => {
  const k = kpis ?? EMPTY_KPIS;
  return (
    num(k.jackpotsCount) === 0 &&
    num(k.jackpotsValue) === 0 &&
    num(k.spins) === 0 &&
    num(k.winPerSpin) === 0 &&
    num(k.moneyWon) === 0
  );
};

/** True when the period payload has real play to show (not empty / all-zero shell). */
const hasRealPlayContent = (
  res: IGetMyPlayResponse | null,
  queryPeriod: MyPlayPeriodQuery
): boolean => {
  if (!res || typeof res !== 'object') return false;
  if (isEmptyResponse(res)) return false;
  const body = res as IGetMyPlayDayBody | IGetMyPlayYearBody;
  if (queryPeriod === 'year') {
    const yearBody = body as IGetMyPlayYearBody;
    const visitsTotal = num(yearBody.visits?.total);
    const titles = parseMostPlayedTitles(yearBody.mostPlayedTitles?.titles);
    const totalSpins = num(yearBody.mostPlayedTitles?.totalSpins);
    return visitsTotal > 0 || (titles.length > 0 && totalSpins > 0);
  }
  const dayBody = body as IGetMyPlayDayBody;
  const hasJackpot = Boolean(dayBody.jackpot);
  const hasMostPlayed = Boolean(dayBody.mostPlayed);
  const games = Array.isArray(dayBody.gamesPlayed) ? dayBody.gamesPlayed : [];
  if (hasJackpot || hasMostPlayed || games.length > 0) return true;
  // All-zero KPIs with no day cards → treat as no play (fall through).
  return !kpisAreAllZero(dayBody.kpis);
};

const EmptySectionLine: React.FC<{ text?: string }> = ({ text = 'No data' }) => (
  <p className="myplay-empty-line">{text}</p>
);

const getRowColor = (ntile: number, insight: InsightValue): string => {
  if (isJackpotInsight(insight)) {
    return floormapJackpotHeatBeanRatings[mapNtileToFloormapHeat(ntile)]?.color ?? 'hsl(0,0%,50%)';
  }
  // eslint-disable-next-line security/detect-object-injection
  return heatBeanRatings[ntile]?.color ?? 'hsl(0,0%,50%)';
};

const getColorWithOpacity = (color: string, opacity = 0.5): string => {
  if (color.startsWith('#')) {
    const hex = color.slice(1);
    const normalized =
      hex.length === 3
        ? hex
            .split('')
            .map((c) => c + c)
            .join('')
        : hex.slice(0, 6);
    const r = parseInt(normalized.slice(0, 2), 16);
    const g = parseInt(normalized.slice(2, 4), 16);
    const b = parseInt(normalized.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
  if (color.startsWith('rgb(')) return color.replace('rgb(', 'rgba(').replace(')', `, ${opacity})`);
  if (color.startsWith('rgba('))
    return color.replace(
      /rgba\((\d+),\s*(\d+),\s*(\d+),\s*[\d.]+\)/,
      `rgba($1, $2, $3, ${opacity})`
    );
  return color;
};

const getCardBackground = (
  ntile: number,
  insightKey: InsightValue,
  isJackpotRow = false
): string => {
  const gradientColor = getRowColor(ntile, insightKey);
  const rowBackground = `linear-gradient(85deg, rgba(29, 33, 46, 0) 52.36%, ${getColorWithOpacity(
    gradientColor,
    0.42
  )} 89.54%)`;
  if (isJackpotRow) {
    return 'url("assets/images/coin-report.png") right center / auto 100% no-repeat, linear-gradient(90deg, rgba(29, 33, 46, 0) 0%, rgba(29, 33, 46, 0) 70%, rgba(255, 189, 96, 0.5) 100%)';
  }
  return rowBackground;
};

type ParseValueFn = (value: number, insight: string) => string | number;
type FormatCurrencyFn = (value: string) => string;
type FormatPercentageFn = (value: number) => string;

const fmtCurrency = formatCurrency as FormatCurrencyFn;
const fmtPercentage = formatPercentage as FormatPercentageFn;

const renderSubstatValue = (item: GamePlayedItem, key: SubstatKey): string => {
  switch (key) {
    case 'jackpot_value':
      return `${fmtCurrency(item.jackpot_value.toString())} | ${item.jackpots}`;
    case 'money_won':
      return fmtCurrency(item.money_won.toString());
    case 'money_played':
      return fmtCurrency(item.money_played.toString());
    case 'return':
      return fmtPercentage(item.return);
    case 'spins':
      return item.spins.toLocaleString();
    case 'avg_money_won_per_spin':
      return fmtCurrency(item.avg_money_won_per_spin.toString());
  }
};

const ntileGradients: Record<number, string> = {
  1: 'linear-gradient(83.89deg, #07BDD1 1.68%, #018CE3 97.65%)',
  2: 'linear-gradient(83.89deg, #89DBDD 1.68%, #74C5EF 97.65%)',
  3: 'linear-gradient(84.88deg, #EBEBEB 5.81%, #FFFFFF 95.16%)',
  4: 'linear-gradient(85.08deg, #F485AE 6.1%, #FC4DAD 97.28%)',
  5: 'linear-gradient(85.08deg, #F04288 6.1%, #F60C8D 97.28%)',
};

const ntileTextColors: Record<number, string> = {
  1: '#05355D',
  2: '#0B5888',
  3: '#636363',
  4: '#FFFFFF',
  5: '#FFFFFF',
  6: '#FFFFFF',
};

const renderInsightValue = (
  value: number,
  ntile: number,
  insightKey: InsightValue,
  suffix?: string
) => {
  const parsedValue = (parseValue as ParseValueFn)(value, insightKey);
  // eslint-disable-next-line security/detect-object-injection
  const background = ntileGradients[ntile] ?? getRowColor(ntile, insightKey);
  // eslint-disable-next-line security/detect-object-injection
  const color = ntileTextColors[ntile] ?? '#FFFFFF';
  return (
    <span className="myplay-insight-value" style={{ background, color }}>
      {parsedValue}
      {suffix && <span className="myplay-insight-value__suffix">{suffix}</span>}
    </span>
  );
};

const getItemInsightValue = (item: GamePlayedItem, filter: GamesPlayedFilterId): number => {
  switch (filter) {
    case 'spins':
      return item.spins;
    case 'money_won':
      return item.money_won;
    case 'avg_money_won_per_spin':
      return item.avg_money_won_per_spin;
    case 'return':
      return item.return;
    case 'money_played':
      return item.money_played;
    case 'jackpot_value':
      return item.jackpot_value;
  }
};

const getItemNtile = (item: GamePlayedItem, filter: GamesPlayedFilterId): number => {
  switch (filter) {
    case 'spins':
      return item.spins_ntile;
    case 'money_won':
      return item.money_won_ntile;
    case 'avg_money_won_per_spin':
      return item.avg_money_won_per_spin_ntile;
    case 'return':
      return item.return_ntile;
    case 'money_played':
      return item.money_played_ntile;
    case 'jackpot_value':
      return item.jackpot_value_ntile;
  }
};

const getGPFilterSuffix = (filter: GamesPlayedFilterId): string | undefined => {
  if (filter === 'spins') return 'Spins';
  return undefined;
};

const formatGPTotal = (
  filter: GamesPlayedFilterId,
  value: number,
  sessions: GamePlayedItem[]
): string => {
  switch (filter) {
    case 'spins':
      return value.toLocaleString();
    case 'money_won':
    case 'money_played':
    case 'jackpot_value':
    case 'avg_money_won_per_spin':
      return fmtCurrency(value.toString());
    case 'return':
      // Weighted Total RTP over sessions in view — never average per-game RTPs.
      return totalRtpLabel(sessions);
  }
};

const StatCardDiagonal = () => (
  <svg
    className="myplay-stats__card-diagonal"
    xmlns="http://www.w3.org/2000/svg"
    width="146"
    height="67"
    viewBox="0 0 146 67"
    fill="none"
    preserveAspectRatio="none"
    aria-hidden="true"
  >
    <path
      d="M132.854 -94H-226V160.751H-4.29225L132.854 -94Z"
      fill="#111111"
      style={{ mixBlendMode: 'plus-lighter' }}
    />
    <path
      d="M2.81377 160.751L139.96 -94H146L8.85388 160.751H2.81377Z"
      fill="#111111"
      style={{ mixBlendMode: 'plus-lighter' }}
    />
  </svg>
);

const MyPlayDashboard: React.FC = () => {
  const casinoSvc = useMemo(() => new CasinoService(), []);
  const [activeTab, setActiveTab] = useState<FilterTab>('today');
  const [gamesPlayedFilter, setGamesPlayedFilter] = useState<GamesPlayedFilterId>('money_won');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showMoreGames, setShowMoreGames] = useState(false);
  const [jackpotsIncluded, setJackpotsIncluded] = useState(false);
  const [menuItem, setMenuItem] = useState<{
    slotNumber: string;
    gameName: string;
    theme: string;
  } | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ x: number; y: number } | null>(null);
  const [showInactiveModal, setShowInactiveModal] = useState(false);

  const [phase, setPhase] = useState<'idle' | 'loading' | 'ready' | 'error' | 'empty'>('idle');
  const [errorKind, setErrorKind] = useState<'linked_card' | 'bad_request' | 'generic' | null>(
    null
  );
  const [kpis, setKpis] = useState<IMyPlayKpis>(EMPTY_KPIS);
  const [summaryYear, setSummaryYear] = useState<number>(new Date().getFullYear());
  /** Raw ISO from year visits.lastVisitDate — kept across day loads for Last Visit header. */
  const [lastVisitDateIso, setLastVisitDateIso] = useState<string | undefined>(undefined);
  /** Fallback playDate from last_visit day cards when visits not on day response. */
  const [dayPlayDateIso, setDayPlayDateIso] = useState<string | undefined>(undefined);
  const [jackpot, setJackpot] = useState<JackpotView | null>(null);
  const [mostPlayed, setMostPlayed] = useState<MostPlayedView | null>(null);
  const [gamesPlayed, setGamesPlayed] = useState<GamePlayedItem[]>([]);
  const [visits, setVisits] = useState<{
    total: number;
    avgPerMonth: number;
    lastVisit: string;
  } | null>(null);
  const [donutTitles, setDonutTitles] = useState<
    Array<{ name: string; value: number; spins: number; color: string }>
  >([]);
  const [donutTotalSpins, setDonutTotalSpins] = useState(0);

  const loadSeq = useRef(0);
  /** Casino id we already resolved a default tab for (skip cascade on manual tab clicks). */
  const resolvedDefaultCasinoRef = useRef<number | undefined>(undefined);
  const prevCasinoIdRef = useRef<number | undefined>(undefined);
  const userSelectedTabRef = useRef(false);
  /** Cascade already fetched+applied; skip duplicate GetMyPlay when setActiveTab re-enters effect. */
  const cascadeApplyRef = useRef<{
    res: IGetMyPlayResponse;
    period: MyPlayPeriodQuery;
  } | null>(null);

  const userInfo = useSelector<IState, IUserInfo | undefined>((s) => s.app.persistedState.userInfo);
  const casinoId = useSelector<IState>((s) => s.app.persistedState.casinoSchema?.kpCasinoPk) as
    | number
    | undefined;
  const casinoSchema = useSelector<IState>((s) => s.app.persistedState.casinoSchema) as
    | Partial<ICasinoSchema>
    | undefined;
  const isInsight = isMyPlayInsight(casinoSchema);
  /** Casino IANA zone (casinoTimezoneNew → timezone → default America/Los_Angeles). */
  const casinoTimeZone = resolveCasinoTimeZone(casinoSchema);
  /** Read in applyResponse without changing its identity (no extra GetMyPlay). */
  const casinoTimeZoneRef = useRef(casinoTimeZone);
  casinoTimeZoneRef.current = casinoTimeZone;

  const history = useHistory();
  const dispatch = useDispatch();
  const casinoCommands = CasinoReduxCommandCreator(dispatch);
  const slotCommands = SlotReduxCommandCreator(dispatch);
  const { handleInsertFavorite, handleDeleteFavorite } = useFavorites({});
  const { handleInsertPlaylist, handleDeletePlaylist } = usePlaylist();
  const { addToSlotHistory } = useSlotDetailHistory();
  const favoritesList = useSelector(selectFavoritesList);
  const playlist = useSelector(selectPlaylist);
  const userId = useSelector(selectUserId) as string;
  const isPlaylistEnabled = featureFlags.playlist;
  const casinoSearchData = useSelector<IState>((s) => s.app.persistedState.casinoSearchData) as
    | ICasinoSearchData
    | undefined;

  // Reuse Explore cache; fetch GetSearchData on miss / wrong casino (HotList pattern).
  // Insight casinos: names come server-side from GetMyPlay (Mongo machines); skip catalog fetch.
  useEffect(() => {
    if (!casinoId) return;
    if (isInsight) return;
    if (casinoSearchData && casinoSearchData.casinoId === casinoId) return;
    const sub = casinoSvc.GetSearchData(casinoId as number).subscribe({
      next(resp) {
        if (resp) {
          const cleaned = resp.DataSet.map((item) => ({
            ...item,
            game_name: getCleanGameName(item.game_name),
          }));
          casinoCommands.SetCasinoSearchData({
            casinoId: casinoId as number,
            data: cleaned,
          });
        }
      },
    });
    return () => sub.unsubscribe();
  }, [casinoId, isInsight]);

  const catalogBySlot = useMemo(() => {
    const map = new Map<string, CatalogNameTheme>();
    // Insight casinos: server-side join in GetMyPlay; do not use GetSearchData.
    if (!casinoId || isInsight) {
      return map;
    }
    if (!casinoSearchData || casinoSearchData.casinoId !== casinoId) {
      return map;
    }
    const rows = casinoSearchData.data ?? [];
    for (const row of rows) {
      const key = String(row.slot_number ?? '').trim();
      if (!key) continue;
      map.set(key, {
        game_name: String(row.game_name ?? ''),
        theme: String(row.theme ?? ''),
        section: String(row.section ?? ''),
      });
    }
    return map;
  }, [casinoId, isInsight, casinoSearchData]);

  const avatarText = userInfo?.aliasInitial2 || userInfo?.aliasInitial || 'P';
  const displayName = userInfo?.aliasName || 'Player';
  const isYearSummary = activeTab === 'year_summary';
  const period = tabToPeriod(activeTab);

  /** Period-aware MY PLAY header subtitle — never show a stale year on Today / Last Visit. */
  const headerSubtitle = useMemo((): string | null => {
    if (activeTab === 'year_summary') {
      return String(summaryYear);
    }
    if (activeTab === 'today') {
      return formatTodayHeaderDate(casinoTimeZone);
    }
    // last_visit: visits.lastVisitDate (cached from year) → day playDate → hide
    const iso = lastVisitDateIso || dayPlayDateIso;
    if (!iso) return null;
    const label = formatVisitDate(iso, casinoTimeZone);
    return label === '—' ? null : label;
  }, [activeTab, summaryYear, lastVisitDateIso, dayPlayDateIso, casinoTimeZone]);

  const displayJackpot = useMemo(
    () => (jackpot ? enrichFromCatalog(jackpot, catalogBySlot) : null),
    [jackpot, catalogBySlot]
  );
  const displayMostPlayed = useMemo(
    () => (mostPlayed ? enrichFromCatalog(mostPlayed, catalogBySlot) : null),
    [mostPlayed, catalogBySlot]
  );
  const displayGamesPlayed = useMemo(
    () => gamesPlayed.map((item) => enrichFromCatalog(item, catalogBySlot)),
    [gamesPlayed, catalogBySlot]
  );
  // Resolve mnum labels when catalog loads (same FE join as jackpot / mostPlayed).
  const displayDonutTitles = useMemo<MostPlayedDonutSlice[]>(
    () =>
      donutTitles.map((entry) => ({
        id: entry.name,
        name: resolveTitleName(entry.name, catalogBySlot),
        value: entry.value,
        spins: entry.spins,
        color: entry.color,
      })),
    [donutTitles, catalogBySlot]
  );

  const activeFilterLabel = GP_FILTERS.find((f) => f.id === gamesPlayedFilter)?.label ?? '$ Won';
  const hasAnyJackpots = displayGamesPlayed.some((item) => item.jackpots > 0);
  const showJackpotsToggle = GP_TOGGLE_FILTERS.includes(gamesPlayedFilter) && hasAnyJackpots;

  const gpTotal = displayGamesPlayed.reduce(
    (sum, item) => sum + getItemInsightValue(item, gamesPlayedFilter),
    0
  );

  const visibleGames = showMoreGames ? displayGamesPlayed : displayGamesPlayed.slice(0, 3);

  const gpDateGroups = visibleGames.reduce<Record<string, GamePlayedItem[]>>((acc, item) => {
    // eslint-disable-next-line security/detect-object-injection
    if (!acc[item.date_group]) acc[item.date_group] = [];
    // eslint-disable-next-line security/detect-object-injection
    acc[item.date_group].push(item);
    return acc;
  }, {});

  const resetPayload = useCallback(() => {
    setKpis(EMPTY_KPIS);
    setJackpot(null);
    setMostPlayed(null);
    setGamesPlayed([]);
    setVisits(null);
    setDonutTitles([]);
    setDonutTotalSpins(0);
    setSummaryYear(new Date().getFullYear());
    setDayPlayDateIso(undefined);
    // Keep lastVisitDateIso so Last Visit header can reuse year visits after an empty day load.
  }, []);

  const applyResponse = useCallback(
    (res: IGetMyPlayResponse, queryPeriod: MyPlayPeriodQuery) => {
      if (isEmptyResponse(res)) {
        resetPayload();
        setPhase('empty');
        return;
      }
      const body = res as IGetMyPlayDayBody | IGetMyPlayYearBody;
      const nextKpis = body.kpis ?? EMPTY_KPIS;
      setKpis({
        jackpotsCount: num(nextKpis.jackpotsCount),
        jackpotsValue: num(nextKpis.jackpotsValue),
        spins: num(nextKpis.spins),
        winPerSpin: num(nextKpis.winPerSpin),
        moneyWon: num(nextKpis.moneyWon),
      });
      const yearFromBody =
        typeof (body as { year?: number }).year === 'number'
          ? Number((body as { year?: number }).year)
          : undefined;
      setSummaryYear(
        yearFromBody && Number.isFinite(yearFromBody) ? yearFromBody : new Date().getFullYear()
      );
      setJackpot(mapJackpotCard(body.jackpot));

      if (queryPeriod === 'year') {
        const yearBody = body as IGetMyPlayYearBody;
        setMostPlayed(null);
        setGamesPlayed([]);
        setDayPlayDateIso(undefined);
        const v = yearBody.visits;
        if (v?.lastVisitDate) {
          setLastVisitDateIso(String(v.lastVisitDate));
        }
        setVisits(
          v
            ? {
                total: num(v.total),
                avgPerMonth: num(v.avgPerMonth),
                lastVisit: formatVisitDate(v.lastVisitDate, casinoTimeZoneRef.current),
              }
            : null
        );
        const titlesPayload = yearBody.mostPlayedTitles;
        const totalSpins = num(titlesPayload?.totalSpins);
        // Keep raw mnum in name; displayDonutTitles resolves via catalogBySlot.
        const titles = parseMostPlayedTitles(titlesPayload?.titles);
        setDonutTotalSpins(totalSpins);
        if (!titles.length || totalSpins <= 0) {
          setDonutTitles([]);
        } else {
          setDonutTitles(
            titles.map((t, i) => ({
              name: t.name,
              spins: t.spins,
              value: Math.round((t.spins / totalSpins) * 1000) / 10,
              // eslint-disable-next-line security/detect-object-injection
              color: DONUT_COLORS[i % DONUT_COLORS.length],
            }))
          );
        }
      } else {
        const dayBody = body as IGetMyPlayDayBody;
        setVisits(null);
        setDonutTitles([]);
        setDonutTotalSpins(0);
        setMostPlayed(mapMostPlayed(dayBody.mostPlayed));
        const list = Array.isArray(dayBody.gamesPlayed) ? dayBody.gamesPlayed : [];
        setGamesPlayed(
          list.map((c, i) => mapGamesPlayedCard(c, i, queryPeriod, casinoTimeZoneRef.current))
        );
        setDayPlayDateIso(pickDayPlayDate(dayBody));
        // Do not clear lastVisitDateIso — day body has no visits; reuse year cache for header.
      }
      setPhase('ready');
    },
    [resetPayload]
  );

  const selectTab = useCallback((tab: FilterTab) => {
    userSelectedTabRef.current = true;
    // Today / Last Visit Games Played default metric: $ Won (Year UI does not use this filter).
    if (tab === 'today' || tab === 'last_visit') {
      setGamesPlayedFilter('money_won');
    }
    setActiveTab(tab);
  }, []);

  useEffect(() => {
    if (!casinoId) {
      loadSeq.current += 1;
      prevCasinoIdRef.current = undefined;
      resolvedDefaultCasinoRef.current = undefined;
      userSelectedTabRef.current = false;
      cascadeApplyRef.current = null;
      resetPayload();
      setLastVisitDateIso(undefined);
      setErrorKind(null);
      setPhase('idle');
      return;
    }

    const casinoChanged = prevCasinoIdRef.current !== casinoId;
    if (casinoChanged) {
      prevCasinoIdRef.current = casinoId;
      resolvedDefaultCasinoRef.current = undefined;
      userSelectedTabRef.current = false;
      cascadeApplyRef.current = null;
      setLastVisitDateIso(undefined);
      setDayPlayDateIso(undefined);
      setGamesPlayedFilter('money_won');
      if (activeTab !== 'today') {
        setActiveTab('today');
        return;
      }
    }

    // Tab caught up after cascade chose last_visit/year — response already applied.
    if (cascadeApplyRef.current) {
      const pending = cascadeApplyRef.current;
      cascadeApplyRef.current = null;
      applyResponse(pending.res, pending.period);
      return;
    }

    const seq = ++loadSeq.current;
    setPhase('loading');
    setErrorKind(null);
    setShowMoreGames(false);

    const normalize = (res: unknown): IGetMyPlayResponse =>
      res && typeof res === 'object'
        ? (res as IGetMyPlayResponse)
        : ({ empty: true } as IGetMyPlayResponse);

    (async () => {
      try {
        const needsDefault =
          resolvedDefaultCasinoRef.current !== casinoId && !userSelectedTabRef.current;

        if (needsDefault) {
          const cascadeTabs: FilterTab[] = ['today', 'last_visit', 'year_summary'];
          let pickTab: FilterTab = 'today';
          let pickPeriod: MyPlayPeriodQuery = 'today';
          let pickRes: IGetMyPlayResponse = { empty: true };

          for (const tab of cascadeTabs) {
            const p = tabToPeriod(tab);
            const res = await firstValueFrom(casinoSvc.GetMyPlay(casinoId, p));
            if (seq !== loadSeq.current) return;
            const normalized = normalize(res);
            if (hasRealPlayContent(normalized, p)) {
              pickTab = tab;
              pickPeriod = p;
              pickRes = normalized;
              break;
            }
            if (tab === 'today') {
              // Keep today's empty shell as the fallback empty-state landing.
              pickRes = normalized;
              pickPeriod = p;
              pickTab = 'today';
            }
          }

          if (seq !== loadSeq.current) return;
          resolvedDefaultCasinoRef.current = casinoId;
          if (pickTab === 'today' || pickTab === 'last_visit') {
            setGamesPlayedFilter('money_won');
          }
          if (pickTab !== activeTab) {
            cascadeApplyRef.current = { res: pickRes, period: pickPeriod };
            setActiveTab(pickTab);
            applyResponse(pickRes, pickPeriod);
            return;
          }
          applyResponse(pickRes, pickPeriod);
          return;
        }

        const res = await firstValueFrom(casinoSvc.GetMyPlay(casinoId, period));
        if (seq !== loadSeq.current) return;
        applyResponse(normalize(res), period);
      } catch (err: any) {
        if (seq !== loadSeq.current) return;
        resetPayload();
        const status = err?.response?.status || err?.status;
        if (status === 403) {
          setErrorKind('linked_card');
        } else if (status === 400) {
          setErrorKind('bad_request');
        } else {
          setErrorKind('generic');
        }
        setPhase('error');
      }
    })();
  }, [casinoId, period, activeTab, casinoSvc, applyResponse, resetPayload]);

  const closeMenu = () => setMenuPosition(null);

  const openMenu = (e: React.MouseEvent, slotNumber: string, gameName: string, theme: string) => {
    e.stopPropagation();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const menuItemCount = isPlaylistEnabled ? 4 : 3;
    const popoverHeight = menuItemCount * 48 + 2 * 13 + 16;
    const safeMargin = 16;
    let x = rect.left - MACHINE_MENU_POPOVER_WIDTH - 4;
    if (x < safeMargin) x = safeMargin;
    let y = rect.top;
    if (y + popoverHeight + safeMargin > window.innerHeight) {
      y = rect.bottom - popoverHeight;
      if (y < safeMargin) y = safeMargin;
    }
    setMenuItem({ slotNumber, gameName, theme });
    setMenuPosition({ x, y });
  };

  const handleMenuAction = (action: 'favorite' | 'playlist' | 'view' | 'filterTitle') => {
    if (!menuItem) return;
    // menuItem.slotNumber is the detail id (mnum for insight).
    const slotNum = menuItem.slotNumber;

    const searchData = (casinoSearchData?.data ?? []) as ISearchInfo[];
    const slotInfo = searchData.find(
      (d) =>
        String(d.mnum ?? '').trim() === slotNum ||
        String(d.slot_number) === slotNum ||
        String(d.floorLocation ?? '') === slotNum
    );
    const isInactive = !menuItem.gameName || (slotInfo ? !slotInfo.is_active : false);

    if (action === 'favorite') {
      if (isInactive) {
        closeMenu();
        setShowInactiveModal(true);
        return;
      }
      if (userId && casinoId) {
        if (isIncludedInFavorites(slotNum, favoritesList)) {
          handleDeleteFavorite(slotNum, userId, String(casinoId), true);
        } else {
          handleInsertFavorite(slotNum, userId, String(casinoId), true);
        }
      }
    } else if (action === 'playlist') {
      if (isInactive) {
        closeMenu();
        setShowInactiveModal(true);
        return;
      }
      if (userId && casinoId) {
        if (isIncludedInPlaylist(slotNum, playlist)) {
          handleDeletePlaylist(slotNum, userId, String(casinoId), true);
        } else {
          handleInsertPlaylist(slotNum, userId, String(casinoId), true);
        }
      }
    } else if (action === 'view') {
      if (isInactive) {
        closeMenu();
        setShowInactiveModal(true);
        return;
      }
      addToSlotHistory(Number(slotNum));
      history.push('/tabs/home');
    } else if (action === 'filterTitle') {
      const titleKey = getCleanGameName(menuItem.gameName);
      const data = (casinoSearchData?.data ?? []) as ISearchInfo[];
      const matches = data.filter((d) => d.game_name === titleKey);
      // Insight: mnum. Legacy: slot_number. Never floorLocation.
      const fromMatches = matches
        .map((m) => searchRowDetailId(m))
        .filter((n): n is number => n !== null);
      const slotNumbers = fromMatches.length
        ? fromMatches
        : Number.isFinite(Number(slotNum))
          ? [Number(slotNum)]
          : [];
      slotCommands.SetSearchMode(true);
      slotCommands.SelectBankFilter(false);
      slotCommands.SelectedSlotsName(titleKey);
      slotCommands.SelectedSlots(slotNumbers.map(String) as unknown as string[]);
      history.push('/tabs/home');
    }

    closeMenu();
  };

  const checkInactive = (slotNumber: string, gameName: string): boolean => {
    const searchData = (casinoSearchData?.data ?? []) as ISearchInfo[];
    const slotInfo = searchData.find(
      (d) =>
        String(d.mnum ?? '').trim() === slotNumber ||
        String(d.slot_number) === slotNumber ||
        String(d.floorLocation ?? '') === slotNumber
    );
    return !gameName || (slotInfo ? !slotInfo.is_active : false);
  };

  const handleDirectFavorite = (e: React.MouseEvent, slotNumber: string, gameName: string) => {
    e.stopPropagation();
    if (checkInactive(slotNumber, gameName)) {
      setShowInactiveModal(true);
      return;
    }
    if (userId && casinoId) {
      if (isIncludedInFavorites(slotNumber, favoritesList)) {
        handleDeleteFavorite(slotNumber, userId, String(casinoId), true);
      } else {
        handleInsertFavorite(slotNumber, userId, String(casinoId), true);
      }
    }
  };

  const handleDirectPlaylist = (e: React.MouseEvent, slotNumber: string, gameName: string) => {
    e.stopPropagation();
    if (checkInactive(slotNumber, gameName)) {
      setShowInactiveModal(true);
      return;
    }
    if (userId && casinoId) {
      if (isIncludedInPlaylist(slotNumber, playlist)) {
        handleDeletePlaylist(slotNumber, userId, String(casinoId), true);
      } else {
        handleInsertPlaylist(slotNumber, userId, String(casinoId), true);
      }
    }
  };

  const jackpotsKpiLabel = `${num(kpis.jackpotsCount).toLocaleString()} | ${fmtCurrency(
    String(num(kpis.jackpotsValue))
  )}`;

  const statusBanner =
    phase === 'loading' ? (
      <p className="myplay-status myplay-status--loading">Loading…</p>
    ) : phase === 'error' ? (
      <p className="myplay-status myplay-status--error">
        {errorKind === 'linked_card'
          ? 'Link a player card at this casino to see My Play.'
          : errorKind === 'bad_request'
          ? 'Could not load My Play for this casino or period.'
          : 'Failed to load My Play. Try again.'}
      </p>
    ) : phase === 'empty' ? (
      <p className="myplay-status myplay-status--empty">No play data for this period yet.</p>
    ) : !casinoId ? (
      <p className="myplay-status myplay-status--empty">Select a casino to see My Play.</p>
    ) : null;

  const showContent = phase === 'ready' || phase === 'empty';

  return (
    <IonPage className="myplay-page">
      <CasinoNavBar hideSearch />
      <IonContent className="myplay-page__content">
        {/* Filter Tabs */}
        <div className="myplay-filter" role="tablist">
          {FILTER_TABS.map((tab) => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`myplay-filter__tab${
                activeTab === tab.id ? ' myplay-filter__tab--active' : ''
              }`}
              onClick={() => selectTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {statusBanner}

        {/* MY PLAY Card */}
        <div className="myplay-card">
          <div className="myplay-header">
            <div className="myplay-header__left">
              <span className="myplay-header__title">MY PLAY</span>
              {headerSubtitle ? (
                <span className="myplay-header__year">{headerSubtitle}</span>
              ) : null}
            </div>
            {SHOW_SHARE && (
              <button className="myplay-header__share" aria-label="Share">
                <IonIcon icon={shareOutline} />
              </button>
            )}
          </div>

          <div className="myplay-profile">
            {userInfo?.avatarId ? (
              <div
                className="myplay-profile__avatar myplay-profile__avatar--photo"
                style={{ backgroundImage: `url(${S3_BASE}/avatar-bg${userInfo.avatarBgId}.png)` }}
              >
                <img
                  src={`${S3_BASE}/avatar${userInfo.avatarId}.png`}
                  alt="profile"
                  className="myplay-profile__avatar-img"
                />
              </div>
            ) : (
              <div className="myplay-profile__avatar">{avatarText}</div>
            )}
            <span className="myplay-profile__name">{displayName}</span>
          </div>

          <div className="myplay-stats">
            <div className="myplay-stats__card">
              <StatCardDiagonal />
              <span className="menu-icon-bubble" />
              <span className="myplay-stats__label">Jackpots</span>
              <span className="myplay-stats__value">{jackpotsKpiLabel}</span>
            </div>
            <div className="myplay-stats__card">
              <StatCardDiagonal />
              <span className="menu-icon-bubble" />
              <span className="myplay-stats__label"># of Spins</span>
              <span className="myplay-stats__value">{num(kpis.spins).toLocaleString()}</span>
            </div>
            <div className="myplay-stats__card">
              <StatCardDiagonal />
              <span className="menu-icon-bubble" />
              <span className="myplay-stats__label">$ W/S</span>
              <span className="myplay-stats__value">
                {fmtCurrency(String(num(kpis.winPerSpin)))}
              </span>
            </div>
            <div className="myplay-stats__card">
              <StatCardDiagonal />
              <span className="menu-icon-bubble" />
              <span className="myplay-stats__label">$ Won</span>
              <span className="myplay-stats__value">{fmtCurrency(String(num(kpis.moneyWon)))}</span>
            </div>
          </div>
        </div>

        {showContent && isYearSummary ? (
          <>
            {/* Year Summary 1. Biggest Jackpot */}
            <div className="myplay-section">
              <div className="myplay-section__heading">
                <IonIcon
                  icon="assets/icon/myplay/star.svg"
                  className="myplay-section__heading-icon"
                />
                <span className="label-heading">Biggest Jackpot</span>
              </div>
              {displayJackpot ? (
                <div
                  className="myplay-jackpot-card"
                  style={{
                    background: getCardBackground(
                      displayJackpot.jackpot_value_ntile,
                      'jackpot_value',
                      true
                    ),
                  }}
                >
                  <span className="jkpt-title">JACKPOT</span>
                  <div className="myplay-jackpot-card__bg" />
                  <span className="menu-icon-bubble" />
                  <div className="myplay-jackpot-card__content">
                    <div className="myplay-jackpot-card__meta">
                      <MyPlayMarqueeText
                        text={displayJackpot.game_name}
                        className="myplay-jackpot-card__game myplay-jackpot-card__game--jackpot"
                      />
                      <MyPlayMarqueeText
                        text={displayJackpot.theme}
                        className="myplay-jackpot-card__theme"
                      />
                      <span className="myplay-jackpot-card__location">
                        <span>{displayJackpot.slot_number}</span>
                        {hasMyPlayBankText(displayJackpot, isInsight) && (
                          <>
                            <span className="myplay-jackpot-card__dot">·</span>
                            <span>Bank {myPlayBankText(displayJackpot, isInsight)}</span>
                          </>
                        )}
                        <IonImg
                          src="assets/icon/myplay/play-list.svg"
                          className="play-icon-list"
                          onClick={(e) =>
                            handleDirectPlaylist(
                              e,
                              myPlayDetailId(displayJackpot),
                              displayJackpot.game_name
                            )
                          }
                        />
                        <IonIcon
                          icon={heartOutline}
                          onClick={(e) =>
                            handleDirectFavorite(
                              e,
                              myPlayDetailId(displayJackpot),
                              displayJackpot.game_name
                            )
                          }
                        />
                      </span>
                    </div>
                    <div className="myplay-jackpot-card__right">
                      {renderInsightValue(
                        displayJackpot.jackpot_value,
                        displayJackpot.jackpot_value_ntile,
                        'jackpot_value'
                      )}
                      <IonIcon
                        icon={ellipsisVertical}
                        className="myplay-jackpot-card__menu-icon myplay-jackpot-card__menu-icon--jackpot"
                        onClick={(e) =>
                          openMenu(
                            e,
                            myPlayDetailId(displayJackpot),
                            displayJackpot.game_name,
                            displayJackpot.theme
                          )
                        }
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <EmptySectionLine />
              )}
            </div>

            {/* Year Summary 2. Visits */}
            <div className="myplay-section">
              <div className="myplay-section__heading">
                <IonIcon
                  icon="assets/icon/myplay/location.svg"
                  className="myplay-section__heading-icon"
                />
                <span className="label-heading">Visits</span>
              </div>
              {visits ? (
                <div className="visits-stat-card">
                  <div className="visits-stat-card__cols">
                    <div className="visits-stat-card__col">
                      <span className="visits-stat-card__number">{visits.total}</span>
                      <span className="visits-stat-card__sublabel">Total</span>
                    </div>
                    <div className="visits-stat-card__divider" />
                    <div className="visits-stat-card__col">
                      <span className="visits-stat-card__number">{visits.avgPerMonth}</span>
                      <span className="visits-stat-card__sublabel">avg / mo</span>
                    </div>
                  </div>
                  <div className="visits-stat-card__footer">
                    <div className="last-visit">Last Visit</div>
                    <strong>{visits.lastVisit}</strong>
                  </div>
                </div>
              ) : (
                <EmptySectionLine />
              )}
            </div>

            {/* Year Summary 3. Most Played Titles */}
            <div className="myplay-section">
              <div className="myplay-section__heading">
                <IonIcon
                  icon="assets/icon/myplay/mostplayed.svg"
                  className="myplay-section__heading-icon"
                />
                <span className="label-heading">Most Played Titles</span>
              </div>
              {displayDonutTitles.length > 0 ? (
                <MostPlayedTitlesDonut slices={displayDonutTitles} totalSpins={donutTotalSpins} />
              ) : (
                <EmptySectionLine />
              )}
            </div>
          </>
        ) : showContent ? (
          <>
            {/* Today / Last Visit 1. Last Jackpot */}
            <div className="myplay-section">
              <div className="myplay-section__heading">
                <IonIcon
                  icon="assets/icon/myplay/star.svg"
                  className="myplay-section__heading-icon"
                />
                <span className="label-heading">Last Jackpot</span>
              </div>
              {displayJackpot ? (
                <div
                  className="myplay-jackpot-card"
                  style={{
                    background: getCardBackground(
                      displayJackpot.jackpot_value_ntile,
                      'jackpot_value',
                      true
                    ),
                  }}
                >
                  <span className="jkpt-title">JACKPOT</span>
                  <div className="myplay-jackpot-card__bg" />
                  <span className="menu-icon-bubble" />
                  <div className="myplay-jackpot-card__content">
                    <div className="myplay-jackpot-card__meta">
                      <MyPlayMarqueeText
                        text={displayJackpot.game_name}
                        className="myplay-jackpot-card__game myplay-jackpot-card__game--jackpot"
                      />
                      <MyPlayMarqueeText
                        text={displayJackpot.theme}
                        className="myplay-jackpot-card__theme"
                      />
                      <span className="myplay-jackpot-card__location">
                        <span>{displayJackpot.slot_number}</span>
                        {hasMyPlayBankText(displayJackpot, isInsight) && (
                          <>
                            <span className="myplay-jackpot-card__dot">·</span>
                            <span>Bank {myPlayBankText(displayJackpot, isInsight)}</span>
                          </>
                        )}
                        <IonImg
                          src="assets/icon/myplay/play-list.svg"
                          className="play-icon-list"
                          onClick={(e) =>
                            handleDirectPlaylist(
                              e,
                              myPlayDetailId(displayJackpot),
                              displayJackpot.game_name
                            )
                          }
                        />
                        <IonIcon
                          icon={heartOutline}
                          onClick={(e) =>
                            handleDirectFavorite(
                              e,
                              myPlayDetailId(displayJackpot),
                              displayJackpot.game_name
                            )
                          }
                        />
                      </span>
                    </div>
                    <div className="myplay-jackpot-card__right">
                      {renderInsightValue(
                        displayJackpot.jackpot_value,
                        displayJackpot.jackpot_value_ntile,
                        'jackpot_value'
                      )}
                      <IonIcon
                        icon={ellipsisVertical}
                        className="myplay-jackpot-card__menu-icon myplay-jackpot-card__menu-icon--jackpot"
                        onClick={(e) =>
                          openMenu(
                            e,
                            myPlayDetailId(displayJackpot),
                            displayJackpot.game_name,
                            displayJackpot.theme
                          )
                        }
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <EmptySectionLine />
              )}
            </div>

            {/* Today / Last Visit 2. Most Played */}
            <div className="myplay-section">
              <div className="myplay-section__heading">
                <IonIcon
                  icon="assets/icon/myplay/mostplayed.svg"
                  className="myplay-section__heading-icon"
                />
                <span className="label-heading">Most Played</span>
              </div>
              {displayMostPlayed ? (
                <div
                  className="myplay-jackpot-card"
                  style={{ background: getCardBackground(displayMostPlayed.spins_ntile, 'spins') }}
                >
                  <div className="myplay-jackpot-card__bg" />
                  <span
                    className="menu-icon-bubble"
                    style={{
                      background: getColorWithOpacity(
                        getRowColor(displayMostPlayed.spins_ntile, 'spins'),
                        1
                      ),
                    }}
                  />
                  <div className="myplay-jackpot-card__content">
                    <div className="myplay-jackpot-card__meta">
                      <MyPlayMarqueeText
                        text={displayMostPlayed.game_name}
                        className="myplay-jackpot-card__game"
                      />
                      <MyPlayMarqueeText
                        text={displayMostPlayed.theme}
                        className="myplay-jackpot-card__theme"
                      />
                      <span className="myplay-jackpot-card__location">
                        <span>{displayMostPlayed.slot_number}</span>
                        {hasMyPlayBankText(displayMostPlayed, isInsight) && (
                          <>
                            <span className="myplay-jackpot-card__dot">·</span>
                            <span>Bank {myPlayBankText(displayMostPlayed, isInsight)}</span>
                          </>
                        )}
                        <IonImg
                          src="assets/icon/myplay/play-list.svg"
                          className="play-icon-list"
                          onClick={(e) =>
                            handleDirectPlaylist(
                              e,
                              myPlayDetailId(displayMostPlayed),
                              displayMostPlayed.game_name
                            )
                          }
                        />
                        <IonIcon
                          icon={heartOutline}
                          onClick={(e) =>
                            handleDirectFavorite(
                              e,
                              myPlayDetailId(displayMostPlayed),
                              displayMostPlayed.game_name
                            )
                          }
                        />
                      </span>
                    </div>
                    <div className="myplay-jackpot-card__right">
                      {renderInsightValue(
                        displayMostPlayed.spins,
                        displayMostPlayed.spins_ntile,
                        'spins',
                        'Spins'
                      )}
                      <IonIcon
                        icon={ellipsisVertical}
                        className="myplay-jackpot-card__menu-icon"
                        style={{
                          color: getColorWithOpacity(
                            getRowColor(displayMostPlayed.spins_ntile, 'spins'),
                            1
                          ),
                        }}
                        onClick={(e) =>
                          openMenu(
                            e,
                            myPlayDetailId(displayMostPlayed),
                            displayMostPlayed.game_name,
                            displayMostPlayed.theme
                          )
                        }
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <EmptySectionLine />
              )}
            </div>

            {/* Today / Last Visit 3. Games Played */}
            <div className="myplay-section">
              <div className="myplay-section__heading">
                <IonIcon
                  icon="assets/icon/myplay/play.svg"
                  className="myplay-section__heading-icon play-icon-play"
                />
                <span className="label-heading">Games Played</span>
              </div>

              {displayGamesPlayed.length === 0 ? (
                <EmptySectionLine />
              ) : (
                <>
                  <div className="gp-total">
                    {/* eslint-disable-next-line security/detect-object-injection */}
                    <span className="gp-total__label">{GP_TOTAL_LABELS[gamesPlayedFilter]}</span>
                    <span className="gp-total__value">
                      {formatGPTotal(gamesPlayedFilter, gpTotal, displayGamesPlayed)}
                    </span>
                  </div>

                  {Object.entries(gpDateGroups).map(([dateGroup, items], groupIndex) => (
                    <div key={dateGroup} className="gp-date-group">
                      <div className="gp-date-separator">
                        <span>{dateGroup}</span>
                        {groupIndex === 0 && showJackpotsToggle && (
                          <div className="gp-controls-bar__toggle">
                            <span className="gp-controls-bar__toggle-label">
                              {jackpotsIncluded ? 'Jackpots Included' : 'Jackpots Excluded'}
                            </span>
                            <IonToggle
                              checked={jackpotsIncluded}
                              onIonChange={(e) => setJackpotsIncluded(e.detail.checked)}
                              className="gp-toggle"
                            />
                          </div>
                        )}
                        {groupIndex === 0 && (
                          <div className="gp-filter-btn-wrap">
                            <button
                              className="gp-filter-btn"
                              onClick={() => setShowFilterDropdown((v) => !v)}
                            >
                              {activeFilterLabel}
                              <IonIcon
                                icon={chevronForwardOutline}
                                className="gp-filter-btn__chevron"
                              />
                            </button>
                            {showFilterDropdown && (
                              <>
                                <div
                                  className="gp-filter-overlay"
                                  onClick={() => setShowFilterDropdown(false)}
                                />
                                <div className="gp-filter-dropdown">
                                  {GP_FILTERS.map((f) => (
                                    <button
                                      key={f.id}
                                      className={`gp-filter-dropdown__item${
                                        gamesPlayedFilter === f.id
                                          ? ' gp-filter-dropdown__item--active'
                                          : ''
                                      }`}
                                      onClick={() => {
                                        setGamesPlayedFilter(f.id);
                                        setShowFilterDropdown(false);
                                      }}
                                    >
                                      {f.label}
                                    </button>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                      {items.map((item) => {
                        const ntile = getItemNtile(item, gamesPlayedFilter);
                        const value = getItemInsightValue(item, gamesPlayedFilter);
                        const suffix = getGPFilterSuffix(gamesPlayedFilter);
                        const isRTPHot = gamesPlayedFilter === 'return' && item.return > 1.0;
                        const heatColor = isRTPHot
                          ? RTP_HOT_COLOR
                          : getRowColor(ntile, gamesPlayedFilter);
                        const substats = getSubstats(gamesPlayedFilter, jackpotsIncluded);
                        const gpCardBg = `linear-gradient(85deg, rgba(29, 33, 46, 0) 52.36%, ${getColorWithOpacity(
                          heatColor,
                          0.42
                        )} 89.54%)`;

                        return (
                          <div
                            key={item.id}
                            className={`myplay-jackpot-card myplay-jackpot-card--gp${
                              isRTPHot ? ' myplay-jackpot-card--hot' : ''
                            }`}
                            style={{ background: gpCardBg }}
                          >
                            <span
                              className="menu-icon-bubble"
                              style={{ background: getColorWithOpacity(heatColor, 1) }}
                            />
                            <div className="myplay-jackpot-card__content myplay-jackpot-card__content--gp">
                              <div className="myplay-jackpot-card__top-row">
                                <div className="myplay-jackpot-card__meta">
                                  <MyPlayMarqueeText
                                    text={item.game_name}
                                    className="myplay-jackpot-card__game"
                                  />
                                  <MyPlayMarqueeText
                                    text={item.theme}
                                    className="myplay-jackpot-card__theme"
                                  />
                                  <span className="myplay-jackpot-card__location">
                                    <span>{item.slot_number}</span>
                                    {hasMyPlayBankText(item, isInsight) && (
                                      <>
                                        <span className="myplay-jackpot-card__dot">·</span>
                                        <span>Bank {myPlayBankText(item, isInsight)}</span>
                                      </>
                                    )}
                                    <IonImg
                                      src="assets/icon/myplay/play-list.svg"
                                      className="play-icon-list"
                                      onClick={(e) =>
                                        handleDirectPlaylist(e, myPlayDetailId(item), item.game_name)
                                      }
                                    />
                                    <IonIcon
                                      icon={heartOutline}
                                      onClick={(e) =>
                                        handleDirectFavorite(e, myPlayDetailId(item), item.game_name)
                                      }
                                    />
                                  </span>
                                </div>
                                <div className="myplay-jackpot-card__right">
                                  <span
                                    className="myplay-insight-value"
                                    style={{
                                      // eslint-disable-next-line security/detect-object-injection
                                      background: isRTPHot
                                        ? heatColor
                                        : ntileGradients[ntile] ?? heatColor,
                                      // eslint-disable-next-line security/detect-object-injection
                                      color: isRTPHot
                                        ? '#FFFFFF'
                                        : ntileTextColors[ntile] ?? '#FFFFFF',
                                    }}
                                  >
                                    {(parseValue as ParseValueFn)(value, gamesPlayedFilter)}
                                    {suffix && (
                                      <span className="myplay-insight-value__suffix">{suffix}</span>
                                    )}
                                  </span>
                                  <IonIcon
                                    icon={ellipsisVertical}
                                    className="myplay-jackpot-card__menu-icon"
                                    style={{ color: getColorWithOpacity(heatColor, 1) }}
                                    onClick={(e) =>
                                      openMenu(e, myPlayDetailId(item), item.game_name, item.theme)
                                    }
                                  />
                                </div>
                              </div>
                              <div className="gp-card-substats">
                                {substats.map((key) => (
                                  <span key={key} className="gp-card-substats__chip">
                                    {/* eslint-disable-next-line security/detect-object-injection */}
                                    <span className="gp-card-substats__label">
                                      {SUBSTAT_LABEL[key]}
                                    </span>
                                    <span className="gp-card-substats__value">
                                      {renderSubstatValue(item, key)}
                                    </span>
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}

                  {displayGamesPlayed.length > 3 && (
                    <button className="gp-show-more" onClick={() => setShowMoreGames((v) => !v)}>
                      {showMoreGames ? 'Show Less' : 'Show More'}
                      <IonIcon
                        icon={chevronDownOutline}
                        className={`gp-show-more__icon${
                          showMoreGames ? ' gp-show-more__icon--up' : ''
                        }`}
                      />
                    </button>
                  )}
                </>
              )}
            </div>
          </>
        ) : null}
      </IonContent>

      {showInactiveModal &&
        createPortal(
          <div className="myplay-inactive-backdrop">
            <div className="myplay-inactive-modal">
              <IonImg
                src="assets/icon/myplay/inactive-slot.svg"
                className="myplay-inactive-modal__icon"
              />
              <p className="myplay-inactive-modal__title">Innactive Slot...</p>
              <p className="myplay-inactive-modal__desc">
                Sorry this slot is no longer on the floor or is currently inactive. If you know this
                is incorrect please notify us so we can fix this issue.
              </p>
              <button
                className="myplay-inactive-modal__btn"
                onClick={() => setShowInactiveModal(false)}
              >
                GO BACK
              </button>
            </div>
          </div>,
          document.body
        )}

      {menuPosition &&
        menuItem &&
        createPortal(
          <>
            <div className="myplay-menu-backdrop" onClick={closeMenu} />
            <div
              role="menu"
              className="myplay-menu-container"
              onClick={(e) => e.stopPropagation()}
              style={{ left: menuPosition.x, top: menuPosition.y }}
            >
              <button
                type="button"
                role="menuitem"
                className="myplay-menu-item"
                onClick={() => handleMenuAction('favorite')}
              >
                <span className="myplay-menu-icon-slot" aria-hidden="true">
                  <FavoriteIcon variant="menu" />
                </span>
                {isIncludedInFavorites(menuItem.slotNumber, favoritesList)
                  ? 'Remove from Favorites'
                  : 'Save to Favorites'}
              </button>
              {isPlaylistEnabled && (
                <button
                  type="button"
                  role="menuitem"
                  className="myplay-menu-item"
                  onClick={() => handleMenuAction('playlist')}
                >
                  <span className="myplay-menu-icon-slot" aria-hidden="true">
                    <PlaylistIcon filled size={14} color="#94A0AA" />
                  </span>
                  {isIncludedInPlaylist(menuItem.slotNumber, playlist)
                    ? 'Remove from Playlist'
                    : 'Save to Playlist'}
                </button>
              )}
              <hr className="myplay-menu-divider" />
              <button
                type="button"
                role="menuitem"
                className="myplay-menu-item"
                onClick={() => handleMenuAction('view')}
              >
                <span className="myplay-menu-icon-slot" aria-hidden="true">
                  <InfoIcon />
                </span>
                View Details
              </button>
              {getCleanGameName(menuItem.gameName) && (
                <>
                  <hr className="myplay-menu-divider" />
                  <button
                    type="button"
                    role="menuitem"
                    className="myplay-menu-filter-item"
                    onClick={() => handleMenuAction('filterTitle')}
                  >
                    <span className="myplay-menu-icon-slot" aria-hidden="true">
                      <IonImg
                        src="assets/icon/myplay/filter-title.svg"
                        className="myplay-menu-filter-title-icon"
                      />
                    </span>
                    Filter by Title
                  </button>
                </>
              )}
            </div>
          </>,
          document.body
        )}
    </IonPage>
  );
};

export default MyPlayDashboard;
